(ns ncr.reports.core-model
  (:require
   [clojure.core.match :refer [match]]
   [clojure.set :as set]
   [uix.hooks.alpha :as uix.hook]
   [uix.core.alpha :as uix]
   [cljs.core.async :as async :refer-macros [go]]
   [globus.core :as glob]
   [taoensso.encore :as e]
   [ncr.backend :as b]
   [ncr.etc.util :as u]
   [ncr.reports.overview]
   [ncr.reports.revenue]
   [ncr.reports.projects]
   [ncr.reports.team]
   [ncr.me :as me]
   [ncr.cube :as cube]
   [ncr.reports.common :as r-common]))

(defn- fragment-ids [reports]
  (->>
    [[:projects-teams :teams]
     [:projects-teams :projects]
     [:cashflow :revenue]
     [:cashflow :costs]]
    (map
      (partial get-in reports))
    (filter identity)
    (map
      (fn [{:keys [axes] :as c}]
        (cube/cell c (mapv (constantly :total) axes))))
    (mapcat (comp keys :context))
    (filter string?)
    (distinct)))

(defn- zero-values? [{:keys [^js actual ^js estimated]}]
  (and
    (or (nil? actual) (.isZero actual))
    (or (nil? estimated) (.isZero estimated))))

(defn- filter-zeroes [rows]
  (->> rows
    (filter
      (fn [{:keys [months-values-fn total-values] :as row}]
        (match row
          {:type :data :filter-zeroes? true}
          (and
            (not (zero-values? total-values))
            (not-every?
              (fn [m]
                (zero-values? (months-values-fn (inc m))))
              (range
                (count r-common/months))))
          _
          true)))
    (map
      (fn [row]
        (update row :sub-rows filter-zeroes)))))

(defn- make-model [rdef me]
  (go
    (u/error-m
      [report
       (async/<!
         (b/!query
           [:report {:definition (u/transit-write rdef)}]))

       report
       (u/transit-read report)

       report
       (merge report {:me me})

       fragments
       (async/<!
         (b/!query
           [:fragment_load_multiple
            {:references
             (fragment-ids report)}
            [:id :name :labels]]))

       fragments
       (u/index-by :id fragments)]

      (let
        [labels-id-to-name (get report :labels {})
         labels-name-to-id (e/invert-map labels-id-to-name)
         expand-labels
         (fn [in]
           (->> in
             (mapcat
               (fn [pattern]
                 (if (glob/glob? pattern)
                   (glob/glob pattern (keys labels-name-to-id)
                     {:ignorecase true})
                   [pattern])))
             (map labels-name-to-id)

             (into #{})))
         labels-income (expand-labels (get rdef :income))
         labels-expense (expand-labels (get rdef :expense))
         labels-directional (set/union labels-expense labels-income)]

        {:meta
         {:report report
          :rdef rdef
          :fragments fragments
          :labels labels-id-to-name
          :labels-income labels-income
          :labels-expense labels-expense
          :labels-directional labels-directional
          :ignore-label? (partial contains? labels-directional)}
         :rows
         (concat
           (ncr.reports.overview/render report {:heading (-> me :cluster :name)})
           (ncr.reports.revenue/render-income report {:heading "Revenue"})
           (ncr.reports.revenue/render-expense report {:heading "Costs"})
           (ncr.reports.projects/render report {:heading "Projects"})
           (ncr.reports.team/render report {:heading "Team"}))}))))

(defn use-model [rdef rparams rmap prefs me]
  (let
    [model (uix/state {:progress? true})
     some-rdef? (some? rdef)]

    (uix/effect!
      (fn []
        (when some-rdef?
          (swap! model assoc :progress? true)
          (go
            (reset! model
              (let [rdef (when (map? rdef) (merge rdef rparams))]
                (u/if-ok [v (<! (make-model rdef me))]
                  {:model v}
                  {:error v}))))))
      [rdef rparams rmap])

    (uix/memo
      (fn []
        (u/update-when @model :model
          (fn [m]
            (-> m
              (assoc-in [:meta :preferences] prefs)
              (update :rows
                (if (get prefs :show-zero-rows?) identity filter-zeroes))))))
      [@model prefs])))

(defn edit-rdef [[report-id k] action]
  (fn [rdef]
    (update rdef :reports
      (fn [reports]
        (->> reports
          (map
            (fn [report]
              (if (= report-id (:id report))
                (-> report
                  (update k
                    (match action
                      [:add new]
                      #(-> % set (conj new))

                      [:replace old new]
                      #(-> % set (disj old) (conj new))

                      [:delete old]
                      #(-> % set (disj old))

                      _
                      identity))

                  (update k vec))
                report)))
          vec)))))
